import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { apiRequest } from "@/lib/queryClient";
import {
  HIGHLIGHT_CARD_TEMPLATES,
  HIGHLIGHT_TEMPLATE_IDS,
  DEFAULT_HIGHLIGHT_IDS,
} from "../../../shared/profilePresets";

interface HighlightsData {
  notes_created: number;
  sessions_total: number;
  streak_best: number;
  arcs_completed: number;
}

interface HighlightsPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIds: string[];
  onSave: (ids: string[]) => void;
}

function HighlightsPickerDialog({ open, onOpenChange, selectedIds, onSave }: HighlightsPickerDialogProps) {
  const [chosen, setChosen] = useState<string[]>(selectedIds);

  function toggle(id: string) {
    setChosen((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  }

  function handleOpen(isOpen: boolean) {
    if (isOpen) setChosen(selectedIds);
    onOpenChange(isOpen);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-w-sm" data-testid="highlights-picker-dialog">
        <DialogHeader>
          <DialogTitle>Choose highlights</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          Select up to 3 · <span className="font-medium">{chosen.length}/3 selected</span>
        </p>
        <div className="space-y-2">
          {HIGHLIGHT_TEMPLATE_IDS.map((id) => {
            const tpl = HIGHLIGHT_CARD_TEMPLATES[id];
            const isChecked = chosen.includes(id);
            const disabled = !isChecked && chosen.length >= 3;
            return (
              <div key={id} className="flex items-center gap-3 rounded-md p-2 hover:bg-muted">
                <Checkbox
                  id={`hl-${id}`}
                  checked={isChecked}
                  disabled={disabled}
                  onCheckedChange={() => toggle(id)}
                  data-testid={`highlight-checkbox-${id}`}
                />
                <Label htmlFor={`hl-${id}`} className="cursor-pointer">
                  {tpl.emoji} {tpl.title}
                  <span className="ml-1 text-xs text-muted-foreground font-normal">
                    ({tpl.subLabel})
                  </span>
                </Label>
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => { onSave(chosen); onOpenChange(false); }} data-testid="save-highlights-btn">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface HighlightsPanelProps {
  selectedIds: string[];
}

export function HighlightsPanel({ selectedIds }: HighlightsPanelProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const queryClient = useQueryClient();

  const displayIds = selectedIds.length > 0 ? selectedIds : DEFAULT_HIGHLIGHT_IDS;

  const { data, isLoading } = useQuery<HighlightsData>({
    queryKey: ["/api/user-profile/highlights-data"],
  });

  const mutation = useMutation({
    mutationFn: (ids: string[]) =>
      apiRequest("PATCH", "/api/user-profile", { highlightCardIds: ids }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["personalised-profile"] });
    },
  });

  const getValue = (id: string): number | null => {
    if (!data) return null;
    return (data as any)[id] ?? null;
  };

  const allZero = displayIds.every((id) => !getValue(id));

  return (
    <div data-testid="highlights-panel">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
          Highlights
        </span>
        <button
          onClick={() => setPickerOpen(true)}
          className="text-muted-foreground hover:text-foreground transition-colors"
          data-testid="edit-highlights-btn"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
        </div>
      ) : allZero && selectedIds.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          Highlights will appear here as you ship more work
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {displayIds.map((id) => {
            const tpl = HIGHLIGHT_CARD_TEMPLATES[id];
            if (!tpl) return null;
            const value = getValue(id);
            return (
              <div
                key={id}
                data-testid={`highlight-card-${id}`}
                className="rounded-lg bg-muted/50 border px-3 py-2 text-center"
              >
                <div className="text-lg">{tpl.emoji}</div>
                <div className="text-lg font-bold leading-tight">
                  {value ?? "—"}
                </div>
                <div className="text-xs text-muted-foreground">{tpl.title}</div>
              </div>
            );
          })}
        </div>
      )}

      <HighlightsPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        selectedIds={selectedIds}
        onSave={(ids) => mutation.mutate(ids)}
      />
    </div>
  );
}
